(() => {
  const MEASUREMENT_ID = "G-EP92WD45GK";
  const SESSION_ID_KEY = "tce_site_session_id";
  const LANDING_CONTEXT_KEY = "tce_site_landing_context";
  const EVENT_NAMES = new Set([
    "tce_action_click",
    "tce_form_submit",
    "tce_section_view",
    "tce_scroll_depth",
    "tce_cart_result"
  ]);
  const ACTIONABLE_SELECTOR = [
    "a[href]",
    "button",
    "summary",
    "input[type='submit']",
    "input[type='button']",
    "[role='button']",
    "shopify-accelerated-checkout",
    "shopify-buy-it-now-button",
    "[data-tce-track]"
  ].join(",");
  const script = document.currentScript;
  const context = {
    pageType: script?.dataset.pageType || "unknown",
    template: script?.dataset.template || "unknown"
  };
  const debugMode = ["tce_site_debug", "tce_diy_debug"].some(
    (key) => new URLSearchParams(window.location.search).get(key) === "1"
  );
  const viewedSections = new Set();
  const sentScrollDepths = new Set();
  const debugEvents = [];
  let analyticsAllowed = false;
  let sectionObserver = null;

  function privacyApi() {
    return window.Shopify?.customerPrivacy;
  }

  function syncConsent() {
    analyticsAllowed = privacyApi()?.analyticsProcessingAllowed?.() === true;
    if (analyticsAllowed) {
      ensureSessionContext();
      observeSections();
    }
  }

  function initConsent() {
    if (privacyApi()) {
      syncConsent();
      return;
    }
    if (typeof window.Shopify?.loadFeatures !== "function") return;
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      (error) => {
        if (!error) syncConsent();
      }
    );
  }

  function sessionGet(key) {
    if (!analyticsAllowed) return "";
    try {
      return window.sessionStorage.getItem(key) || "";
    } catch (_error) {
      return "";
    }
  }

  function sessionSet(key, value) {
    if (!analyticsAllowed) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // Events remain usable when storage is unavailable.
    }
  }

  function safePath(value) {
    if (!value) return "";
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (_error) {
      return "";
    }
  }

  function safeHost(value) {
    if (!value) return "";
    try {
      return new URL(value, window.location.origin).hostname;
    } catch (_error) {
      return "";
    }
  }

  function scrubText(value, fallback = "unlabelled") {
    const text = String(value || "")
      .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, "[redacted]")
      .replace(/\+?\d[\d\s().-]{7,}\d/g, "[redacted]")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 100);
    return text || fallback;
  }

  function safeToken(value, fallback = "unknown") {
    const token = String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 80);
    return token || fallback;
  }

  function trafficSource() {
    if (!document.referrer) return "direct";
    const host = safeHost(document.referrer);
    if (!host) return "unknown";
    if (host === window.location.hostname) return "internal";
    if (/google\.|bing\.|yahoo\.|duckduckgo\.|ecosia\./i.test(host)) return "organic";
    if (/facebook\.|instagram\.|pinterest\.|tiktok\.|youtube\./i.test(host)) return "social";
    return "referral";
  }

  function ensureSessionContext() {
    let sessionId = sessionGet(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = window.crypto?.randomUUID?.() || `site-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionSet(SESSION_ID_KEY, sessionId);
    }
    if (!sessionGet(LANDING_CONTEXT_KEY)) {
      const params = new URLSearchParams(window.location.search);
      sessionSet(LANDING_CONTEXT_KEY, JSON.stringify({
        landing_path: window.location.pathname,
        landing_referrer_host: safeHost(document.referrer),
        traffic_source: trafficSource(),
        utm_source: scrubText(params.get("utm_source"), ""),
        utm_medium: scrubText(params.get("utm_medium"), ""),
        utm_campaign: scrubText(params.get("utm_campaign"), "")
      }));
    }
  }

  function landingContext() {
    try {
      return JSON.parse(sessionGet(LANDING_CONTEXT_KEY) || "{}");
    } catch (_error) {
      return {};
    }
  }

  function globalParameters() {
    ensureSessionContext();
    return {
      page_path: window.location.pathname,
      page_type: context.pageType,
      template_name: context.template,
      site_session_id: sessionGet(SESSION_ID_KEY),
      referrer_path: safePath(document.referrer),
      ...landingContext()
    };
  }

  function emit(eventName, parameters = {}) {
    if (!EVENT_NAMES.has(eventName)) return false;
    if (!analyticsAllowed || typeof window.gtag !== "function") return false;
    const payload = {
      ...globalParameters(),
      ...parameters,
      send_to: MEASUREMENT_ID,
      event_transport: "beacon",
      ...(debugMode ? { debug_mode: true } : {})
    };
    if (debugMode) debugEvents.push({ eventName, parameters: payload });
    window.gtag("event", eventName, payload);
    return true;
  }

  function sectionKey(element) {
    const section = element.closest(".shopify-section, [id^='shopify-section-'], header, footer, main");
    if (!section) return "unknown";
    if (section.matches("header")) return "header";
    if (section.matches("footer")) return "footer";
    if (section.matches("main")) return "main";
    return safeToken(section.id.replace(/^shopify-section-/, ""), "section");
  }

  function destinationType(destination) {
    if (!destination) return "none";
    if (destination.origin !== window.location.origin) return "external";
    if (destination.pathname === window.location.pathname && destination.hash) return "anchor";
    return "internal";
  }

  function formType(form) {
    const action = safePath(form?.action || "");
    if (action.includes("/cart/add")) return "product_add_to_cart";
    if (action === "/cart") return "cart_update_or_checkout";
    if (action.includes("/search")) return "search";
    if (action.includes("/contact")) return form?.classList.contains("newsletter-form") ? "newsletter" : "contact";
    if (action.includes("/account")) return "account";
    if (action.includes("/localization")) return "localization";
    return safeToken(form?.getAttribute("data-type") || form?.name || form?.id, "other");
  }

  function actionName(element, destination) {
    const form = element.closest("form");
    const hrefPath = destination?.pathname || "";
    const name = element.getAttribute("name") || "";
    const classes = element.className?.toString?.() || "";
    if (name === "checkout" || /\/checkout\/?$/.test(hrefPath) || element.matches("shopify-accelerated-checkout, shopify-buy-it-now-button")) return "checkout_start";
    if (formType(form) === "product_add_to_cart" || name === "add" || /product-form__submit|quick-add__submit/.test(classes)) return "product_add_to_cart";
    if (/cart-remove|remove/i.test(`${element.tagName} ${name} ${classes}`) && (hrefPath === "/cart" || element.closest("cart-items, cart-drawer-items"))) return "cart_remove";
    if (element.closest("quantity-input") || /quantity__button/.test(classes)) return "quantity_change";
    if (hrefPath === "/cart") return "cart_open";
    if (/search/.test(classes) || element.closest(".header__search, search-form, predictive-search") || hrefPath === "/search") return hrefPath === "/search" ? "search_result_or_submit" : "search_control";
    if (hrefPath.startsWith("/account")) return "account_open";
    if (element.closest("facet-filters-form, .facets-container")) return "filter_or_sort";
    if (element.closest("localization-form")) return "localization_control";
    if (element.closest(".newsletter-form")) return "newsletter_submit";
    if (element.matches("summary")) return "details_toggle";
    if (element.closest("header")) return destination ? "header_navigation" : "header_control";
    if (element.closest("footer")) return destination ? "footer_navigation" : "footer_control";
    if (destination) return destination.origin === window.location.origin ? "internal_navigation" : "external_navigation";
    return safeToken(element.dataset.tceTrack || name || element.id || element.tagName, "button_click");
  }

  function elementLabel(element) {
    return scrubText(
      element.dataset.tceLabel ||
      element.getAttribute("aria-label") ||
      element.getAttribute("title") ||
      (element.tagName === "INPUT" ? element.value : element.textContent)
    );
  }

  function elementKey(element) {
    const classToken = element.classList ? [...element.classList].find((name) => !/^(active|open|focus|loading|hidden|disabled|animate)/.test(name)) : "";
    return safeToken(
      element.dataset.tceTrack || element.id || element.getAttribute("name") || classToken || element.tagName,
      "action"
    );
  }

  function handleActionClick(event) {
    const origin = event.target instanceof Element ? event.target : event.target?.parentElement;
    const element = origin?.closest?.(ACTIONABLE_SELECTOR);
    if (!element || element.matches(":disabled, [aria-disabled='true']")) return;
    let destination = null;
    const href = element.getAttribute("href");
    if (href) {
      try {
        destination = new URL(href, window.location.origin);
      } catch (_error) {
        destination = null;
      }
    }
    emit("tce_action_click", {
      action_name: actionName(element, destination),
      action_kind: safeToken(element.getAttribute("role") || element.tagName),
      element_key: elementKey(element),
      element_label: elementLabel(element),
      section_key: sectionKey(element),
      destination_path: destination?.pathname || "",
      destination_type: destinationType(destination)
    });
  }

  function handleFormSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;
    emit("tce_form_submit", {
      form_type: formType(form),
      form_key: safeToken(form.dataset.tceTrack || form.id || form.name, "form"),
      section_key: sectionKey(form),
      destination_path: safePath(form.action)
    });
  }

  function observeSections() {
    if (!analyticsAllowed || sectionObserver || !("IntersectionObserver" in window)) return;
    const sections = document.querySelectorAll("main .shopify-section[id]");
    sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.01) return;
        const key = safeToken(entry.target.id.replace(/^shopify-section-/, ""), "section");
        if (viewedSections.has(key)) return;
        viewedSections.add(key);
        emit("tce_section_view", {
          section_key: key,
          section_index: [...sections].indexOf(entry.target) + 1
        });
      });
    }, { threshold: 0.01 });
    sections.forEach((section) => sectionObserver.observe(section));
  }

  function handleScroll() {
    if (!analyticsAllowed) return;
    const available = document.documentElement.scrollHeight - window.innerHeight;
    if (available <= 0) return;
    const percent = Math.min(100, Math.round((window.scrollY / available) * 100));
    [25, 50, 75, 90].forEach((depth) => {
      if (percent < depth || sentScrollDepths.has(depth)) return;
      sentScrollDepths.add(depth);
      emit("tce_scroll_depth", { scroll_percent: depth });
    });
  }

  function subscribeToCartEvents() {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS !== "object") return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (detail = {}) => {
      emit("tce_cart_result", {
        cart_result: "success",
        cart_source: safeToken(detail.source || "theme"),
        variant_id: scrubText(detail.variantId, "")
      });
    });
    subscribe(PUB_SUB_EVENTS.cartError, (detail = {}) => {
      emit("tce_cart_result", {
        cart_result: "error",
        cart_source: safeToken(detail.source || "theme"),
        error_type: safeToken(detail.errors || detail.message || "unknown")
      });
    });
  }

  window.TCESiteAnalytics = Object.freeze({
    emit,
    allowedEvents: Object.freeze([...EVENT_NAMES]),
    measurementId: MEASUREMENT_ID,
    consentAllowed: () => analyticsAllowed,
    debugEvents: () => debugEvents.map((event) => ({ ...event, parameters: { ...event.parameters } })),
    debugMode
  });

  document.addEventListener("visitorConsentCollected", syncConsent);
  document.addEventListener("click", handleActionClick, true);
  document.addEventListener("submit", handleFormSubmit, true);
  window.addEventListener("scroll", handleScroll, { passive: true });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      observeSections();
      subscribeToCartEvents();
    }, { once: true });
  } else {
    observeSections();
    subscribeToCartEvents();
  }
  initConsent();
})();
